import { reduce, last } from "lodash";

import { OUTER, POSTCODE, SECTOR } from "../consts/layers";
import {
  CHOICE_PLAN_TYPE,
  DEFAULT_PLAN_TYPE,
  LOCATIONS_PLAN_TYPE,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
  ROBOTS_PLAN_TYPE,
} from "../consts/planTypes";

import { Postcode } from "./postcode";

export const calculateTotals = ({
  overlayData,
  planType,
  validPostcodes = [],
}) => {
  if (!overlayData) return;
  const fullData = [...overlayData];
  const cumulativeTotals = { OUTER: {}, SECTOR: {}, POSTCODE: {} };

  fullData.forEach((timeBand) => {
    const cadTotals = { OUTER: {}, SECTOR: {}, POSTCODE: {} };
    timeBand.timeBand =
      timeBand.timeBand.length > 5
        ? timeBand.timeBand.substr(0, 5)
        : timeBand.timeBand;

    const { overlayOuter, overlaySector, overlayPostcode } = processStops({
      stops: timeBand.overlayStops,
      cadTotals,
      cumulativeTotals,
      planType,
      validPostcodes,
    });

    timeBand.overlayOuter = overlayOuter;
    timeBand.overlaySector = overlaySector;
    timeBand.overlayPostcode = overlayPostcode;
  });
  return fullData;
};

const processStops = ({
  stops = [],
  cadTotals,
  cumulativeTotals,
  planType,
  validPostcodes = [],
}) => {
  const overlayOuter = {};
  const overlaySector = {};
  const overlayPostcode = {};

  stops.forEach((stop) => {
    if (!stop.postcode) return;

    if (!includeStopBasedOnType({ planType, stop })) return;
    const postcode = new Postcode(stop.postcode);
    if (!validPostcodes.includes(postcode.toPostcode())) return;

    if (!cadTotals.POSTCODE[postcode.toPostcode()])
      cadTotals.POSTCODE[postcode.toPostcode()] = { stops: 0, parcels: 0 };
    if (!cadTotals.SECTOR[postcode.toSector()])
      cadTotals.SECTOR[postcode.toSector()] = { stops: 0, parcels: 0 };
    if (!cadTotals.OUTER[postcode.toOuter()])
      cadTotals.OUTER[postcode.toOuter()] = { stops: 0, parcels: 0 };

    const postcodeCadTotals = cadTotals.POSTCODE[postcode.toPostcode()];
    const sectorCadTotals = cadTotals.SECTOR[postcode.toSector()];
    const outerCadTotals = cadTotals.OUTER[postcode.toOuter()];

    postcodeCadTotals.stops += 1;
    postcodeCadTotals.parcels += stop.stopParcels;
    sectorCadTotals.stops += 1;
    sectorCadTotals.parcels += stop.stopParcels;
    outerCadTotals.stops += 1;
    outerCadTotals.parcels += stop.stopParcels;
  });

  const processLevel = ({
    level = POSTCODE,
    postcodeKey = "postcode",
    overlay,
  }) => {
    Object.keys(cadTotals[level]).forEach((postcode) => {
      if (!cumulativeTotals[level][postcode])
        cumulativeTotals[level][postcode] = { stops: 0, parcels: 0 };
      cumulativeTotals[level][postcode].stops +=
        cadTotals[level][postcode]?.stops || 0;
      cumulativeTotals[level][postcode].parcels +=
        cadTotals[level][postcode]?.parcels || 0;
    });
    Object.keys(cumulativeTotals[level]).forEach((postcode) => {
      overlay[postcode] = {
        [postcodeKey]: postcode,
        stopsCadTotal: cadTotals[level][postcode]?.stops || 0,
        parcelsCadTotal: cadTotals[level][postcode]?.parcels || 0,
        stopsCumulativeTotal: cumulativeTotals[level][postcode]?.stops || 0,
        parcelsCumulativeTotal: cumulativeTotals[level][postcode]?.parcels || 0,
      };
    });
  };

  processLevel({
    level: POSTCODE,
    postcodeKey: "postcode",
    overlay: overlayPostcode,
  });
  processLevel({
    level: SECTOR,
    postcodeKey: "postcodeSector",
    overlay: overlaySector,
  });
  processLevel({
    level: OUTER,
    postcodeKey: "postcodeOuter",
    overlay: overlayOuter,
  });

  return { overlayOuter, overlaySector, overlayPostcode };
};

export const includeStopBasedOnType = ({ planType, stop }) => {
  if (
    [OVERRIDES_PLAN_TYPE, LOCATIONS_PLAN_TYPE, PICKUP_PLAN_TYPE].includes(
      stop.stopType
    )
  )
    return false;
  else if (planType === ROBOTS_PLAN_TYPE) return !!stop.robotStop;
  else if (planType === CHOICE_PLAN_TYPE)
    return !!(stop.choiceStop && stop.routeType !== ROBOTS_PLAN_TYPE);
  else if (!!!planType || stop.stopType === planType)
    return ![ROBOTS_PLAN_TYPE, CHOICE_PLAN_TYPE].includes(stop.routeType);
  else
    return (
      planType === DEFAULT_PLAN_TYPE &&
      (stop.routeType === DEFAULT_PLAN_TYPE || !stop.routeType)
    );
};

export const overlayDetailsAsObject = (overlayDetails) => {
  if (!overlayDetails) return null;
  return overlayDetails.reduce((prev, curr) => {
    prev[curr.timeBand] = curr;
    return prev;
  }, {});
};

export const copyOverlayDetails = (overlayDetails) =>
  reduce(
    overlayDetails,
    (prev, detail) => {
      prev.push({
        overlayOuter: { ...detail.overlayOuter },
        overlaySector: { ...detail.overlaySector },
        overlayPostcode: { ...detail.overlayPostcode },
        overlayStops: [...detail.overlayStops],
        timeBand: detail.timeBand,
      });
      return prev;
    },
    []
  );

export const getOverlayBeforeTimeBand = (overlayDetails = [], timeBand) =>
  getLastOverlayTimeBand(
    overlayDetails.filter(
      (overlay) =>
        overlay.timeBand.replace("23:", "-23:") <
        timeBand.replace("23:", "-23:")
    )
  );

export const getLastOverlayTimeBand = (overlayDetails = []) =>
  last(
    [...overlayDetails].sort(({ timeBand: a }, { timeBand: b }) =>
      a.replace("23:", "-23:").localeCompare(b.replace("23:", "-23:"))
    )
  )?.timeBand;

export const resetVolumeOverrideStopType = ({ volumeData }) => {
  volumeData.overlayDetails = volumeData.overlayDetails.map(
    (overlayTimeBand) => ({
      ...overlayTimeBand,
      overlayStops: overlayTimeBand.overlayStops.map((stop) => {
        if (stop.stopType === OVERRIDES_PLAN_TYPE && stop.originalStopType) {
          stop.stopType = stop.originalStopType;
          delete stop.routeCode;
          delete stop.originalStopType;
        }
        return stop;
      }),
    })
  );
};

export const resetVolumePickupStopType = ({ volumeData }) => {
  volumeData.overlayDetails = volumeData.overlayDetails.map(
    (overlayTimeBand) => ({
      ...overlayTimeBand,
      overlayStops: overlayTimeBand.overlayStops.map((stop) => {
        if (stop.stopType === PICKUP_PLAN_TYPE && stop.originalStopType) {
          stop.stopType = stop.originalStopType;
          delete stop.routeCode;
          delete stop.originalStopType;
        }
        return stop;
      }),
    })
  );
};
